// Поле с подписью, подсказкой и ошибкой (components/Field/README.md; ux.md, раздел 13).
// Подпись всегда над полем; у необязательного поля к ней добавляется «— по желанию» (optional).
// Подсказка или ошибка — одна строка под полем, ошибка заменяет подсказку.
// Строка не переносится и не сдвигает раскладку: длинный текст сокращается многоточием. Полностью
// он слышен в программе чтения (aria-describedby), виден при наведении (title), а с клавиатуры
// и касанием (README «Доступность», T163) — в слое поверх строки (FieldLine).
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useEffect, useId, useRef, useState } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { CircleAlert, Search } from 'lucide-react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import { useTruncation } from '../lib/truncation.ts';
import { Kbd } from './Kbd.tsx';

/**
 * Что получает элемент управления от Field. Обязательность — только aria-required: проверку
 * при отправке и тексты ошибок делает форма (ux.md, раздел 13), всплывающие подсказки браузера
 * не нужны.
 */
export interface FieldControlProps {
  id: string;
  'aria-describedby': string;
  'aria-invalid': true | undefined;
  'aria-required': true | undefined;
  disabled: boolean | undefined;
}

export interface FieldProps {
  label: ReactNode;
  /** Одна строка подсказки цветом подписи. */
  hint?: ReactNode;
  /** Текст ошибки: что сделать и пример. Заменяет подсказку. */
  error?: ReactNode;
  required?: boolean;
  /**
   * Необязательное поле: подпись «Комментарий — по желанию» (ux.md, раздел 13; README «Тексты»).
   * Плейсхолдер остаётся примером значения: «например, 20.10.2026».
   */
  optional?: boolean;
  disabled?: boolean;
  /** Почему поле нельзя изменить — показывается вместо подсказки. */
  disabledReason?: ReactNode;
  id?: string;
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}

function hasText(node: ReactNode): boolean {
  return node != null && node !== false && node !== '';
}

export function Field({
  label,
  hint,
  error,
  required,
  optional,
  disabled,
  disabledReason,
  id,
  className,
  children,
}: FieldProps) {
  const autoId = useId();
  const controlId = id ?? `f${autoId}`;
  const lineId = `${controlId}-line`;
  const hasError = hasText(error);
  return (
    <div className={cx('t-field', disabled && 'kp-field-disabled', className)}>
      <label id={`${controlId}-label`} className="t-field-label" htmlFor={controlId}>
        {label}
        {optional ? (
          <>
            {' '}
            <span className="kp-field-opt">— по желанию</span>
          </>
        ) : null}
      </label>
      {children({
        id: controlId,
        'aria-describedby': lineId,
        'aria-invalid': hasError ? true : undefined,
        'aria-required': required ? true : undefined,
        disabled,
      })}
      <FieldLine
        id={lineId}
        controlId={controlId}
        error={hasError ? error : undefined}
        hint={disabled && disabledReason ? disabledReason : hint}
        focusable={disabled}
      />
    </div>
  );
}

export interface FieldLineProps {
  /** id строки: на него указывает aria-describedby элемента управления. */
  id: string;
  /** id элемента управления: фокус на нём показывает сокращённый текст целиком. */
  controlId: string;
  /** Ошибка — цветом color-bad со значком; заменяет подсказку. */
  error?: ReactNode;
  hint?: ReactNode;
  /**
   * Элемент управления не получает фокус (неактивен): до сокращённой строки доходят Tab по ней
   * самой.
   */
  focusable?: boolean;
}

/**
 * Слой полного текста ложится точно на строку: текст в нём — там же и тем же шрифтом. Рамка
 * и отступ слоя — снаружи строки: LAYER_INSET по бокам, LAYER_INSET / 2 сверху (kp.css,
 * .kp-field-full). Высота строки — 18 px (.kp-field-line).
 */
const LINE_HEIGHT = 18;
const LAYER_INSET = 8;

/**
 * Строка подсказки или ошибки под полем (ux.md, раздел 13): одна, без переноса, раскладку
 * не сдвигает. Сокращённый многоточием текст (README «Доступность», T163) виден целиком в слое:
 * пока фокус на элементе управления (или на самой строке неактивного поля) и после касания
 * строки. Слой ложится на место строки и раскрывается вниз поверх того, что под полем —
 * раскладка не двигается. Esc закрывает только его (верхний слой), касание слоя или нажатие мимо
 * поля — тоже. Программе чтения слой не нужен: текст строки — в описании поля.
 */
export function FieldLine({ id, controlId, error, hint, focusable = false }: FieldLineProps) {
  const hasError = hasText(error);
  const line = hasError ? error : hint;
  const lineRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const { truncated, text, width } = useTruncation(textRef, line);

  // Фокус на элементе управления или на строке — не на кнопке внутри поля: у кнопки календаря
  // своя подсказка, слой бы её закрыл.
  const [focused, setFocused] = useState(false);
  // Строку коснулись (щёлкнули) — слой открыт и без фокуса в поле.
  const [pinned, setPinned] = useState(false);
  // Слой закрыт Esc, касанием или нажатием мимо — до нового фокуса, касания строки или текста.
  const [dismissed, setDismissed] = useState(false);
  const [shownText, setShownText] = useState(text);
  if (shownText !== text) {
    setShownText(text);
    setPinned(false);
    setDismissed(false);
  }

  useEffect(() => {
    const own = (node: EventTarget | null) =>
      node instanceof Element && (node.id === controlId || node.id === id);
    // Фокус пришёл в поле — слой снова открывается, даже если его закрыли; ушёл — слоя нет.
    const track = (next: boolean) => (event: FocusEvent) => {
      if (!own(event.target) || own(event.relatedTarget)) return;
      setFocused(next);
      setDismissed(false);
    };
    const onFocusIn = track(true);
    const onFocusOut = track(false);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    // Фокус мог прийти раньше подписки: autoFocus, первое поле окна.
    const sync = () => setFocused(own(document.activeElement));
    sync();
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [controlId, id]);

  const open = truncated && !dismissed && (focused || pinned);
  const close = () => {
    setPinned(false);
    setDismissed(true);
  };
  const lineClass = hasError ? 't-field-err' : 't-field-hint';
  const lineText = hasError ? (
    <>
      <Icon icon={CircleAlert} />
      {error}
    </>
  ) : (
    line
  );

  return (
    // Слой — подсказка Radix без собственного фокуса: ловушка фокуса окна не снимается, Esc
    // закрывает только верхний слой. Открытием управляет строка, а не наведение. Слой — рядом
    // со строкой, а не внутри неё: нажатие в слое не доходит до обработчиков строки.
    <RadixTooltip.Provider delayDuration={0} disableHoverableContent>
      <RadixTooltip.Root open={open} onOpenChange={() => undefined}>
        {/* Строка живая: ошибка, появившаяся после отправки, озвучивается (FR-041б). */}
        <div
          ref={lineRef}
          id={id}
          className={cx('kp-field-line', truncated && 'is-truncated')}
          aria-live="polite"
          tabIndex={truncated && focusable ? 0 : undefined}
          // Касание строки не уводит фокус из поля и открывает слой.
          onPointerDown={truncated ? (event) => event.preventDefault() : undefined}
          onClick={
            truncated
              ? () => {
                  setPinned(true);
                  setDismissed(false);
                }
              : undefined
          }
        >
          {hasText(line) ? (
            <RadixTooltip.Trigger asChild>
              <span
                ref={textRef}
                className={lineClass}
                title={truncated ? text : undefined}
                // Наведение не открывает слой и не закрывает чужие подсказки: при наведении
                // полный текст — в title.
                onPointerMove={(event) => event.preventDefault()}
              >
                {lineText}
              </span>
            </RadixTooltip.Trigger>
          ) : null}
        </div>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side="bottom"
            align="start"
            sideOffset={-(LINE_HEIGHT + LAYER_INSET / 2)}
            alignOffset={-LAYER_INSET}
            // Слой всегда на месте строки: не перескакивает наверх и не закрывает само поле.
            avoidCollisions={false}
            // Окно и панель въезжают с анимацией: слой следует за строкой на каждом кадре.
            updatePositionStrategy="always"
            className="kp-field-full"
            // Ширина — по раскладке строки, а не по её видимому размеру (--radix-tooltip-trigger-
            // width): на въезде окна видимый размер меняется каждый кадр, и слой бы за ним гнался.
            style={{ width: width + 2 * LAYER_INSET }}
            role={undefined}
            aria-hidden="true"
            onEscapeKeyDown={close}
            // Нажатие в само поле (ввод, подпись, строка) слой не закрывает.
            onPointerDownOutside={(event) => {
              const target = event.target;
              const field = lineRef.current?.parentElement;
              if (target instanceof Node && field?.contains(target)) event.preventDefault();
              else close();
            }}
            // Касание слоя закрывает его и не уводит фокус из поля.
            onPointerDown={(event) => event.preventDefault()}
            onClick={close}
          >
            <span className={lineClass}>{lineText}</span>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

type FieldShellProps = Omit<FieldProps, 'children'>;

export interface TextFieldProps
  extends
    FieldShellProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'disabled' | 'required' | 'className'> {
  ref?: Ref<HTMLInputElement>;
  inputClassName?: string;
}

/** Текстовое поле высотой size-field. */
export function TextField({
  label,
  hint,
  error,
  required,
  optional,
  disabled,
  disabledReason,
  id,
  className,
  inputClassName,
  type = 'text',
  ...input
}: TextFieldProps) {
  return (
    <Field {...{ label, hint, error, required, optional, disabled, disabledReason, id, className }}>
      {(control) => (
        <input {...input} {...control} type={type} className={cx('t-input', inputClassName)} />
      )}
    </Field>
  );
}

export interface TextAreaFieldProps
  extends
    FieldShellProps,
    Omit<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      'id' | 'disabled' | 'required' | 'className'
    > {
  ref?: Ref<HTMLTextAreaElement>;
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  optional,
  disabled,
  disabledReason,
  id,
  className,
  ...textarea
}: TextAreaFieldProps) {
  return (
    <Field {...{ label, hint, error, required, optional, disabled, disabledReason, id, className }}>
      {(control) => <textarea {...textarea} {...control} className="t-input" />}
    </Field>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps
  extends
    FieldShellProps,
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'disabled' | 'required' | 'className'> {
  options: readonly SelectOption[];
  ref?: Ref<HTMLSelectElement>;
}

/** Выбор из списка: системный select в оформлении поля. */
export function SelectField({
  label,
  hint,
  error,
  required,
  optional,
  disabled,
  disabledReason,
  id,
  className,
  options,
  ...select
}: SelectFieldProps) {
  return (
    <Field {...{ label, hint, error, required, optional, disabled, disabledReason, id, className }}>
      {(control) => (
        <select {...select} {...control} className="t-input">
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Невидимая подпись поля. */
  label: string;
  /** Клавиша справа: «/». */
  shortcut?: string;
  ref?: Ref<HTMLInputElement>;
}

/** Поле поиска: иконка слева, клавиша справа (t-inwrap). */
export function SearchField({ label, shortcut, className, ...input }: SearchFieldProps) {
  return (
    <div className={cx('t-inwrap', className)}>
      <Icon icon={Search} className="t-ico" />
      <input {...input} type="search" aria-label={label} className="t-input" />
      {shortcut ? <Kbd aria-hidden="true">{shortcut}</Kbd> : null}
    </div>
  );
}
